import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { HappyPayBankAccount } from '../interfaces/happy-pay.interface';

const apiUrl = environment.apiUrl + 'user/happy_pay_bank_account';

@Injectable({
  providedIn: 'root',
})
export class HappyPayBankAccountService {
  happyPayBankAccounts: BehaviorSubject<any> = new BehaviorSubject(null);
  editBankAccState: BehaviorSubject<any> = new BehaviorSubject(null);

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private datePipe: DatePipe
  ) {}

  setEditBankAccState(data: HappyPayBankAccount | null) {
    this.editBankAccState.next(data);
  }

  getEditBankAccState() {
    return this.editBankAccState.asObservable();
  }

  private fetchHappyPayBankAccounts() {
    const body = {
      item_per_page: 100,
      current_page: 1,
    };
    return this.http
      .post(apiUrl + '/search', body)
      .pipe(
        map((res: any) => {
          let bankAccounts: HappyPayBankAccount[] =
            res.data.happypay_bank_accounts;
          bankAccounts.forEach((account: any) => {
            if (account.created_at)
              account.formatted_created_at = this.datePipe.transform(
                new Date(account.created_at),
                'dd MMM yyyy, HH:mm'
              );
            if (account.updated_at)
              account.formatted_update_at = this.datePipe.transform(
                new Date(account.updated_at),
                'dd MMM yyyy, HH:mm'
              );
          });
          return bankAccounts;
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  getHappyPayBankAccounts() {
    if (!this.happyPayBankAccounts.value) {
      this.fetchHappyPayBankAccounts().subscribe((data) => {
        this.happyPayBankAccounts.next(data);
        return data;
      });
    }
    return this.happyPayBankAccounts.asObservable();
  }

  getHappyPayBankAccount(bankAccountId: number | string) {
    return this.http
      .get<HappyPayBankAccount>(apiUrl + '_bank_account/' + bankAccountId)
      .pipe(
        map((res: any) => {
          const bankAccount: HappyPayBankAccount = res.data;
          return bankAccount;
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  addHappyPayBankAccount(newBankAccount: HappyPayBankAccount) {
    return this.http
      .post(apiUrl, newBankAccount)
      .pipe(
        map((res: any) => {
          let bankAccount: HappyPayBankAccount = res.data;
          if (bankAccount.created_at)
            bankAccount.formatted_created_at =
              this.datePipe.transform(
                new Date(bankAccount.created_at),
                'dd MMM yyyy, HH:mm'
              ) || '';
          if (bankAccount.updated_at)
            bankAccount.formatted_update_at =
              this.datePipe.transform(
                new Date(bankAccount.updated_at),
                'dd MMM yyyy, HH:mm'
              ) || '';
          const bankAccounts: HappyPayBankAccount[] = [
            ...this.happyPayBankAccounts.value,
            bankAccount,
          ];
          this.happyPayBankAccounts.next(bankAccounts);
          this.handleSuccess('Berhasil menambah akun bank baru');
          return bankAccounts;
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  editHappyPayBankAccount(
    bankAccountId: number | string,
    updatedBankAccount: HappyPayBankAccount
  ) {
    return this.http
      .put(apiUrl + '/' + bankAccountId, updatedBankAccount)
      .pipe(
        map((res: any) => {
          let bankAccount: HappyPayBankAccount = res.data;
          if (bankAccount.created_at)
            bankAccount.formatted_created_at =
              this.datePipe.transform(
                new Date(bankAccount.created_at),
                'dd MMM yyyy, HH:mm'
              ) || '';
          if (bankAccount.updated_at)
            bankAccount.formatted_update_at =
              this.datePipe.transform(
                new Date(bankAccount.updated_at),
                'dd MMM yyyy, HH:mm'
              ) || '';
          if (this.happyPayBankAccounts.value) {
            this.handleEditBankAccount(bankAccount);
          }
          this.handleSuccess('Berhasil mengubah data akun bank');
          return bankAccount;
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  deleteHappyPayBankAccount(bankAccountId: number) {
    return this.http
      .delete(apiUrl + '/' + bankAccountId)
      .pipe(
        map((res: any) => {
          const bankAccounts: HappyPayBankAccount[] =
            this.happyPayBankAccounts.value.filter(
              (account: any) => account.id !== bankAccountId
            );
          this.happyPayBankAccounts.next(bankAccounts);
          this.handleSuccess('Berhasil menghapus akun bank');
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  private handleEditBankAccount(updatedBankAccount: HappyPayBankAccount) {
    const bankAccounts: HappyPayBankAccount[] = [
      ...this.happyPayBankAccounts.value,
    ];
    const bankAccountIndex: number = bankAccounts.findIndex(
      (account) => account.id === updatedBankAccount.id
    );
    bankAccounts[bankAccountIndex] = updatedBankAccount;
    this.happyPayBankAccounts.next(bankAccounts);
    return bankAccounts;
  }

  private handleSuccess(message: any) {
    this.messageService.clear();
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: message,
    });
  }
}
